import { useEffect, useRef } from "react";
import type { EmblaCarouselType } from "embla-carousel";

export type CarouselInteractionSource = "arrow" | "dot" | "swipe" | "api";

export type CarouselSlideChange = {
	index: number;
	total: number;
	source: CarouselInteractionSource;
};

/**
 * Reports each selected-slide change with what triggered it.
 * Returns `markSource`, to call right before a programmatic scroll.
 */
export const useSlideChange = (
	api: EmblaCarouselType | undefined,
	onSlideChange?: (change: CarouselSlideChange) => void
) => {
	const sourceRef = useRef<CarouselInteractionSource>("api");

	const markSource = (source: CarouselInteractionSource) => {
		sourceRef.current = source;
	};

	useEffect(() => {
		if (!api) {
			return undefined;
		}

		const handlePointerDown = () => markSource("swipe");

		const handleSelect = () => {
			onSlideChange?.({
				index: api.selectedScrollSnap(),
				total: api.scrollSnapList().length,
				source: sourceRef.current,
			});
			markSource("api");
		};

		api.on("pointerDown", handlePointerDown);
		api.on("select", handleSelect);

		return () => {
			api.off("pointerDown", handlePointerDown);
			api.off("select", handleSelect);
		};
	}, [api, onSlideChange]);

	return { markSource };
};
